import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { useUserRankings } from '../../../hooks/analytics/useUtilizationAnalytics';
import { useSession } from '../../../hooks/auth/useSession';
import { useCallback, memo } from 'react';
import { BaseAnalyticsCard } from '../shared/BaseAnalyticsCard';
import { ItemModalProps } from '../shared/types';
import { UserRankingChart } from './UserRankingChart';
import { UtilizationUserRankingTable } from './UserRankingTable';
import { TopUsersModal } from './top-users-modal';
import { UserDetailModal } from './user-detail-modal';
import { CHART_CONFIG } from '../config';

interface TopUsersCardProps {
  filters: BaseAnalyticsFilter;
}

function UserDetailModalWrapper({
  isOpen,
  onClose,
  item,
  filters,
}: ItemModalProps<UserRankingDto, BaseAnalyticsFilter>) {
  if (!item) return null;

  return <UserDetailModal user={item} filters={filters} isOpen={isOpen} onClose={onClose} />;
}

export const TopUsersCard = memo(({ filters }: TopUsersCardProps) => {
  const { session } = useSession();
  const isLumenaAdmin = !!session?.isglobal;
  const renderChart = useCallback(
    (
      data: UserRankingDto[],
      metric: string,
      onCardClick?: () => void,
      onItemClick?: (user: UserRankingDto) => void,
    ) => {
      const chart = (
        <UserRankingChart
          data={data}
          height={380}
          showSessions={metric === 'sessions'}
          showMinutes={metric === 'minutes'}
          maxItems={10}
          onBarClick={onItemClick}
        />
      );

      return (
        <div
          onClick={e => {
            const target = e.target as HTMLElement;
            const isChartBar =
              target.closest('[class*="nivo"]') &&
              (target.tagName === 'rect' || target.closest('rect'));

            if (!isChartBar && onCardClick) {
              onCardClick();
            }
          }}
          className="cursor-pointer"
          style={{ minHeight: '380px' }}
        >
          {chart}
        </div>
      );
    },
    [],
  );

  const renderTable = useCallback(
    (data: UserRankingDto[], onItemClick?: (user: UserRankingDto) => void) => (
      <UtilizationUserRankingTable
        data={data}
        onItemClick={onItemClick}
        maxRows={8}
        compactView={true}
        isLumenaAdmin={isLumenaAdmin}
      />
    ),
    [isLumenaAdmin],
  );

  return (
    <BaseAnalyticsCard
      title="Top Members"
      subtitle="Click to view all member rankings"
      filters={filters}
      dataHook={(f: BaseAnalyticsFilter) => useUserRankings(f, 'sessions', 20)}
      renderChart={renderChart}
      renderTable={renderTable}
      emptyMessage="No member data available"
      accent={CHART_CONFIG.COLORS.PRIMARY}
      DetailModal={TopUsersModal}
      ItemModal={UserDetailModalWrapper}
    />
  );
});

TopUsersCard.displayName = 'TopUsersCard';
